/**
 * Alt Yüklenici / Taşeron — Kesintiler (Standalone Modül)
 * =======================================================
 * Yemek İaşe, Barınma/Kamp, ISG Ceza, Temizlik, Ekipman, KKD ve
 * kullanıcı tanımlı kategorilerde kesinti kaydı + Excel toplu import.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, PencilSimple, TrashSimple, MagnifyingGlass, WarningOctagon,
  Tag, Buildings, HardHat, CurrencyCircleDollar, CalendarBlank,
  FileXls, DownloadSimple, UploadSimple, CheckCircle, WarningCircle,
  X, ListChecks, ArrowLeft, MapPin,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { smartTitleCaseTR } from "@/lib/textFormat";
import { SortableTh, useSortableRows } from "@/components/SortableTh";

// ---------- Yardımcı: Turkish sayı input'u ----------
const formatTR = (s) => {
  if (s === "" || s == null) return "";
  const n = Number(String(s).replace(/\./g, "").replace(",", "."));
  if (!isFinite(n)) return "";
  const [intPart, decPart] = String(n).split(".");
  const withDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return decPart ? `${withDots},${decPart}` : withDots;
};
const parseTR = (s) => String(s || "").replace(/\./g, "").replace(",", ".");

function MoneyInput({ value, onChange, "data-testid": testId, ...rest }) {
  const [display, setDisplay] = React.useState(() => formatTR(value));
  const focusedRef = React.useRef(false);
  React.useEffect(() => {
    if (!focusedRef.current) setDisplay(formatTR(value));
  }, [value]);
  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^\d,.]/g, "");
    const canonical = parseTR(raw);
    const hasTrailingComma = raw.endsWith(",");
    const decimalHalf = raw.match(/,(\d*)$/);
    const intPart = canonical.split(".")[0] || "";
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    let out = intFormatted;
    if (hasTrailingComma) out += ",";
    else if (decimalHalf && decimalHalf[1] !== undefined && raw.includes(",")) {
      out = intFormatted + "," + decimalHalf[1];
    }
    setDisplay(out);
    onChange(canonical);
  };
  return (
    <Input type="text" inputMode="decimal" value={display}
           onFocus={() => { focusedRef.current = true; }}
           onBlur={() => { focusedRef.current = false; setDisplay(formatTR(value)); }}
           onChange={handleChange}
           data-testid={testId}
           {...rest} />
  );
}

const fmtTL = (n) => new Intl.NumberFormat("tr-TR", {
  style: "currency", currency: "TRY", maximumFractionDigits: 2,
}).format(Number(n) || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

const emptyForm = {
  subcontractor_id: "",
  site_id: "",
  category_id: "",
  date: new Date().toISOString().slice(0, 10),
  description: "",
  unit: "adet",
  quantity: "",
  unit_price: "",
  notes: "",
};

export default function SubDeductions() {
  const { sites, activeSiteId } = useSites();
  const { siteId: urlSiteId } = useParams();
  const isSiteScoped = !!urlSiteId;
  const currentSite = useMemo(
    () => (sites || []).find((s) => s.id === urlSiteId),
    [sites, urlSiteId],
  );
  const confirm = useConfirm();

  const [rows, setRows] = useState([]);
  const [totalAmt, setTotalAmt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [cats, setCats] = useState([]);

  // Filters
  const [q, setQ] = useState("");
  const [fSub, setFSub] = useState("all");
  const [fCat, setFCat] = useState("all");
  const [fSite, setFSite] = useState(urlSiteId || activeSiteId || "all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  // Dialogs
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [catDlgOpen, setCatDlgOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importBusy, setImportBusy] = useState(false);

  // URL'de siteId varsa filtreyi ona kilitle; yoksa SiteContext aktifini takip et.
  useEffect(() => {
    if (isSiteScoped) setFSite(urlSiteId);
    else setFSite(activeSiteId || "all");
  }, [isSiteScoped, urlSiteId, activeSiteId]);

  const loadCats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sub-deduction-categories`);
      setCats(data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  const loadSubs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/subcontractors`);
      const items = Array.isArray(data) ? data : (data.items || []);
      setSubs(items);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  const loadRows = useCallback(async () => {
    // Şantiye scoped görünümde URL'deki siteId hazır olmadan yükleme yapma
    if (isSiteScoped && !urlSiteId) return;
    setLoading(true);
    try {
      const params = {};
      if (fSub !== "all") params.subcontractor_id = fSub;
      if (fCat !== "all") params.category_id = fCat;
      // URL scoped ise site_id daima URL'den
      if (isSiteScoped) params.site_id = urlSiteId;
      else if (fSite !== "all") params.site_id = fSite;
      if (fFrom) params.date_from = fFrom;
      if (fTo) params.date_to = fTo;
      if (q) params.q = q;
      const { data } = await axios.get(`${API}/sub-deductions`, { params });
      setRows(data.items || []);
      setTotalAmt(data.total_amount || 0);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [fSub, fCat, fSite, fFrom, fTo, q, isSiteScoped, urlSiteId]);

  useEffect(() => { loadCats(); loadSubs(); }, [loadCats, loadSubs]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const computedTotal = useMemo(() => {
    const qty = Number(form.quantity || 0);
    const price = Number(form.unit_price || 0);
    return qty * price;
  }, [form.quantity, form.unit_price]);

  const { sort, toggleSort, sortedRows } = useSortableRows(rows, {
    defaultKey: "date", defaultDir: "desc",
    getters: {
      date: (r) => r.date || "",
      site_code: (r) => r.site_code || "",
      subcontractor_name: (r) => r.subcontractor_name || "",
      category_name: (r) => r.category_name || "",
      description: (r) => r.description || "",
      quantity: (r) => Number(r.quantity) || 0,
      unit_price: (r) => Number(r.unit_price) || 0,
      total_amount: (r) => Number(r.total_amount) || 0,
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      site_id: isSiteScoped
        ? urlSiteId
        : (fSite !== "all" ? fSite : (sites[0]?.id || "")),
    });
    setDlgOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      subcontractor_id: r.subcontractor_id,
      site_id: r.site_id,
      category_id: r.category_id,
      date: r.date,
      description: r.description || "",
      unit: r.unit || "adet",
      quantity: String(r.quantity || ""),
      unit_price: String(r.unit_price || ""),
      notes: r.notes || "",
    });
    setDlgOpen(true);
  };

  const save = async () => {
    if (!form.subcontractor_id) { toast.error("Taşeron seçin"); return; }
    if (!form.site_id) { toast.error("Şantiye seçin"); return; }
    if (!form.category_id) { toast.error("Kategori seçin"); return; }
    if (!(Number(form.quantity) > 0)) { toast.error("Miktar 0'dan büyük olmalı"); return; }
    if (!(Number(form.unit_price) > 0)) { toast.error("Birim fiyat 0'dan büyük olmalı"); return; }
    try {
      const payload = {
        ...form,
        description: smartTitleCaseTR(form.description),
        notes: smartTitleCaseTR(form.notes),
        quantity: Number(form.quantity),
        unit_price: Number(form.unit_price),
      };
      if (editing) {
        await axios.put(`${API}/sub-deductions/${editing}`, payload);
        toast.success("Kesinti güncellendi");
      } else {
        await axios.post(`${API}/sub-deductions`, payload);
        toast.success("Kesinti oluşturuldu");
      }
      setDlgOpen(false);
      loadRows();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (r) => {
    const ok = await confirm({
      title: "Kesinti Silinsin mi?",
      description: `${r.category_name} — ${fmtTL(r.total_amount)} tutarındaki kesinti silinecek.`,
      confirmText: "Sil", variant: "danger",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/sub-deductions/${r.id}`);
      toast.success("Kesinti silindi");
      loadRows();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Excel import handlers
  const uploadPreview = async () => {
    if (!importFile) { toast.error("Önce bir dosya seçin"); return; }
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await axios.post(
        `${API}/sub-deductions/import-preview`, fd
      );
      setImportPreview(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setImportBusy(false); }
  };

  const commitImport = async () => {
    try {
      const { data } = await axios.post(
        `${API}/sub-deductions/import-commit`,
        { rows: importPreview.rows });
      toast.success(`${data.created} kesinti kaydı oluşturuldu`);
      setImportOpen(false);
      setImportPreview(null); setImportFile(null);
      loadRows();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Shell>
      <div className="space-y-4" data-testid="sub-deductions-page">
        {/* Şantiye Scope Header (URL /taseron/kesintiler/:siteId) */}
        {isSiteScoped && (
          <div className="flex items-center justify-between gap-4 p-3 bg-rose-50/60 border border-rose-200 rounded-none sm:rounded-sm"
               data-testid="sub-ded-site-scope-header">
            <div className="flex items-center gap-3 min-w-0">
              <Link to="/taseron/kesintiler"
                    className="p-1.5 rounded-full bg-white border border-rose-200 text-rose-700 hover:bg-rose-100 transition"
                    data-testid="sub-ded-back-to-index">
                <ArrowLeft size={14} weight="bold" />
              </Link>
              <MapPin size={18} weight="duotone" className="text-rose-700 shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-rose-700 font-bold">
                  Aktif Şantiye
                </div>
                <div className="text-sm font-bold text-[#1B4776] truncate">
                  <span className="font-mono">{currentSite?.code || urlSiteId}</span>
                  {currentSite?.name && <span className="ml-2 text-slate-700">— {currentSite.name}</span>}
                </div>
              </div>
            </div>
            <Link to="/taseron/kesintiler"
                  className="text-xs text-rose-700 hover:underline shrink-0"
                  data-testid="sub-ded-change-site">
              Şantiye Değiştir
            </Link>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-widest text-slate-500 uppercase font-bold">
              Alt Yüklenici Yönetimi
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1B4776] flex items-center gap-2 mt-1">
              <WarningOctagon size={26} weight="duotone" className="text-rose-600" />
              Taşeron Kesintileri
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              Yemek, barınma, ISG, temizlik, ekipman ve KKD kesintileri — tek tek veya toplu Excel ile.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setCatDlgOpen(true)}
                    className="rounded-none sm:rounded-sm"
                    data-testid="ded-categories-btn">
              <Tag size={14} className="mr-1" /> Kategoriler
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)}
                    className="rounded-none sm:rounded-sm"
                    data-testid="ded-import-btn">
              <FileXls size={14} className="mr-1" /> Excel'den İçe Aktar
            </Button>
            <Button onClick={openCreate}
                    className="bg-rose-600 hover:bg-rose-700 text-white rounded-none sm:rounded-sm"
                    data-testid="ded-create-btn">
              <Plus size={14} weight="bold" className="mr-1" /> Yeni Kesinti
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Kayıt Sayısı" value={rows.length}
                    icon={ListChecks} accent="text-[#1B4776]" />
          <StatCard label="Toplam Kesinti" value={fmtTL(totalAmt)}
                    icon={CurrencyCircleDollar} accent="text-rose-700" />
          <StatCard label="Farklı Taşeron"
                    value={new Set(rows.map(r => r.subcontractor_id)).size}
                    icon={HardHat} accent="text-amber-700" />
          <StatCard label="Kategori Sayısı" value={cats.length}
                    icon={Tag} accent="text-emerald-700" />
        </div>

        {/* Filters */}
        <Card className="p-3 rounded-none sm:rounded-sm border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <div className="col-span-2 md:col-span-2 relative">
              <MagnifyingGlass size={14}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Açıklama / not ara..."
                     className="pl-7 rounded-none sm:rounded-sm"
                     data-testid="ded-search" />
            </div>
            <Select value={fSub} onValueChange={setFSub}>
              <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="ded-filter-sub">
                <SelectValue placeholder="Taşeron" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Taşeronlar</SelectItem>
                {subs.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={fCat} onValueChange={setFCat}>
              <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="ded-filter-cat">
                <SelectValue placeholder="Kategori" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kategoriler</SelectItem>
                {cats.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)}
                   className="rounded-none sm:rounded-sm" data-testid="ded-filter-from" />
            <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)}
                   className="rounded-none sm:rounded-sm" data-testid="ded-filter-to" />
          </div>
        </Card>

        {/* Table */}
        <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <SortableTh label="Tarih" k="date" sort={sort} onSort={toggleSort} className="px-3 py-2 text-left" />
                  <SortableTh label="Şantiye" k="site_code" sort={sort} onSort={toggleSort} className="px-3 py-2 text-left" />
                  <SortableTh label="Taşeron" k="subcontractor_name" sort={sort} onSort={toggleSort} className="px-3 py-2 text-left" />
                  <SortableTh label="Kategori" k="category_name" sort={sort} onSort={toggleSort} className="px-3 py-2 text-left" />
                  <SortableTh label="Açıklama" k="description" sort={sort} onSort={toggleSort} className="px-3 py-2 text-left" />
                  <SortableTh label="Miktar" k="quantity" sort={sort} onSort={toggleSort} className="px-3 py-2 text-right" align="right" />
                  <SortableTh label="Birim Fiyat" k="unit_price" sort={sort} onSort={toggleSort} className="px-3 py-2 text-right" align="right" />
                  <SortableTh label="Toplam" k="total_amount" sort={sort} onSort={toggleSort} className="px-3 py-2 text-right" align="right" />
                  <th className="px-3 py-2 text-right w-24">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="text-center py-6 text-slate-500">Yükleniyor...</td></tr>
                )}
                {!loading && sortedRows.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-10 text-slate-400">
                    Kayıt yok. "Yeni Kesinti" veya "Excel'den İçe Aktar" ile başlayın.
                  </td></tr>
                )}
                {sortedRows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`ded-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{fmtDate(r.date)}</td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-[10px] font-bold text-[#1B4776]">{r.site_code}</div>
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.subcontractor_name}</td>
                    <td className="px-3 py-2">
                      <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase bg-rose-50 text-rose-700 rounded-sm">
                        {r.category_name}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <div className="whitespace-pre-wrap">{smartTitleCaseTR(r.description)}</div>
                      {r.notes && (
                        <div className="text-[10px] text-slate-400 mt-0.5 whitespace-pre-wrap">
                          {smartTitleCaseTR(r.notes)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {r.quantity.toLocaleString("tr-TR")} <span className="text-slate-400 text-xs">{r.unit}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtTL(r.unit_price)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-rose-700">{fmtTL(r.total_amount)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(r)}
                                className="p-1.5 text-slate-500 hover:text-[#1B4776] hover:bg-slate-100 rounded-sm"
                                data-testid={`ded-edit-${r.id}`}>
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => del(r)}
                                className="p-1.5 text-slate-500 hover:text-rose-700 hover:bg-rose-50 rounded-sm"
                                data-testid={`ded-delete-${r.id}`}>
                          <TrashSimple size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Yeni/Düzenle Dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-2xl rounded-none sm:rounded-sm max-h-[92vh] overflow-y-auto"
                       data-testid="ded-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1B4776]">
              {editing ? "Kesinti Düzenle" : "Yeni Kesinti Kaydı"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tarih *</Label>
              <Input type="date" value={form.date}
                     onChange={(e) => setForm({ ...form, date: e.target.value })}
                     className="rounded-none sm:rounded-sm" data-testid="ded-form-date" />
            </div>
            <div>
              <Label>Şantiye *</Label>
              <Select value={form.site_id}
                      onValueChange={(v) => setForm({ ...form, site_id: v })}
                      disabled={isSiteScoped}>
                <SelectTrigger className="rounded-none sm:rounded-sm disabled:opacity-100 disabled:bg-slate-50" data-testid="ded-form-site">
                  <SelectValue placeholder="Şantiye seçin" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <span className="font-mono text-xs mr-2">{s.code}</span>{s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isSiteScoped && (
                <div className="text-[10px] text-slate-500 mt-1">
                  Şantiye URL'den kilitlidir. Değiştirmek için "Şantiye Değiştir" butonuna dönün.
                </div>
              )}
            </div>
            <div className="col-span-2">
              <Label>Taşeron / Alt Yüklenici *</Label>
              <Select value={form.subcontractor_id}
                      onValueChange={(v) => setForm({ ...form, subcontractor_id: v })}>
                <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="ded-form-sub">
                  <SelectValue placeholder="Taşeron seçin" />
                </SelectTrigger>
                <SelectContent>
                  {subs.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Kesinti Kategorisi *</Label>
              <Select value={form.category_id}
                      onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="ded-form-cat">
                  <SelectValue placeholder="Kategori seçin" />
                </SelectTrigger>
                <SelectContent>
                  {cats.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button type="button"
                onClick={() => { setDlgOpen(false); setCatDlgOpen(true); }}
                className="text-xs text-[#1B4776] hover:underline mt-1"
                data-testid="ded-form-new-cat">
                + Yeni kategori ekle
              </button>
            </div>
            <div className="col-span-2">
              <Label>Kesinti Kalemi / Açıklama</Label>
              <Input value={form.description}
                     onChange={(e) => setForm({ ...form, description: e.target.value })}
                     placeholder="Örn: Şubat ayı yemek bedeli, Baret takmama cezası..."
                     className="rounded-none sm:rounded-sm" data-testid="ded-form-desc" />
            </div>
            <div>
              <Label>Birim</Label>
              <Input value={form.unit}
                     onChange={(e) => setForm({ ...form, unit: e.target.value })}
                     placeholder="adet / öğün / gün / kg..."
                     className="rounded-none sm:rounded-sm" data-testid="ded-form-unit" />
            </div>
            <div>
              <Label>Miktar *</Label>
              <MoneyInput value={form.quantity}
                          onChange={(v) => setForm({ ...form, quantity: v })}
                          data-testid="ded-form-qty" />
            </div>
            <div>
              <Label>Birim Fiyat (₺) *</Label>
              <MoneyInput value={form.unit_price}
                          onChange={(v) => setForm({ ...form, unit_price: v })}
                          data-testid="ded-form-price" />
            </div>
            <div>
              <Label>Toplam Tutar</Label>
              <div className="h-10 px-3 flex items-center rounded-none sm:rounded-sm border border-slate-200 bg-slate-50 font-mono font-bold text-rose-700"
                   data-testid="ded-form-total">
                {fmtTL(computedTotal)}
              </div>
            </div>
            <div className="col-span-2">
              <Label>Not (opsiyonel)</Label>
              <Textarea value={form.notes} rows={2}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        className="rounded-none sm:rounded-sm" data-testid="ded-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}
                    className="rounded-none sm:rounded-sm">Vazgeç</Button>
            <Button onClick={save}
                    className="bg-rose-600 hover:bg-rose-700 text-white rounded-none sm:rounded-sm"
                    data-testid="ded-form-save">
              {editing ? "Güncelle" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kategoriler Dialog */}
      <CategoriesDialog open={catDlgOpen} onOpenChange={setCatDlgOpen}
        cats={cats} onChange={loadCats} />

      {/* Excel Import Dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => {
        setImportOpen(v);
        if (!v) { setImportPreview(null); setImportFile(null); }
      }}>
        <DialogContent className="max-w-5xl rounded-none sm:rounded-sm max-h-[92vh] overflow-y-auto"
                       data-testid="ded-import-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1B4776] flex items-center gap-2">
              <FileXls size={20} weight="duotone" className="text-emerald-700" />
              Excel'den Toplu Kesinti İçe Aktar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Adım 1 - Şablon */}
            <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
              <div className="p-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-slate-800">1. Şablonu indirin</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Boş şablonu indirin, satırları doldurun; kolonları veya başlıkları değiştirmeyin.
                  </div>
                </div>
                <Button variant="outline" onClick={() => {
                  window.open(`${API}/sub-deductions/import-template.xlsx`, "_blank");
                }} className="rounded-none sm:rounded-sm"
                   data-testid="ded-import-tpl">
                  <DownloadSimple size={14} weight="bold" className="mr-1" />
                  Şablonu İndir (.xlsx)
                </Button>
              </div>
            </Card>

            {/* Adım 2 - Yükleme */}
            <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
              <div className="p-4">
                <div className="text-sm font-bold text-slate-800 mb-2">2. Doldurulmuş dosyayı yükleyin</div>
                <div className="flex items-center gap-2">
                  <Input type="file" accept=".xlsx,.xls"
                         onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                         className="rounded-none sm:rounded-sm"
                         data-testid="ded-import-file" />
                  <Button onClick={uploadPreview}
                          disabled={!importFile || importBusy}
                          className="bg-[#1B4776] hover:bg-[#153a63] text-white rounded-none sm:rounded-sm"
                          data-testid="ded-import-preview-btn">
                    <UploadSimple size={14} weight="bold" className="mr-1" />
                    {importBusy ? "Yükleniyor..." : "Yükle & Önizle"}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Adım 3 - Preview */}
            {importPreview && (
              <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-slate-800">3. Önizleme</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Toplam <span className="font-bold">{importPreview.total}</span> satır;
                      <span className="font-bold text-emerald-700"> {importPreview.valid} geçerli</span>
                      {importPreview.errors > 0 && (
                        <>, <span className="font-bold text-rose-700">{importPreview.errors} hatalı</span></>
                      )}
                    </div>
                  </div>
                  <Button onClick={commitImport} disabled={importPreview.valid === 0}
                          className="bg-rose-600 hover:bg-rose-700 text-white rounded-none sm:rounded-sm"
                          data-testid="ded-import-commit">
                    <CheckCircle size={14} weight="bold" className="mr-1" />
                    {importPreview.valid} Kesintiyi Onayla ve Ekle
                  </Button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-100">
                      <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                        <th className="px-2 py-1.5 text-left">#</th>
                        <th className="px-2 py-1.5 text-left">Tarih</th>
                        <th className="px-2 py-1.5 text-left">Şantiye</th>
                        <th className="px-2 py-1.5 text-left">Taşeron</th>
                        <th className="px-2 py-1.5 text-left">Kategori</th>
                        <th className="px-2 py-1.5 text-left">Açıklama</th>
                        <th className="px-2 py-1.5 text-right">Miktar</th>
                        <th className="px-2 py-1.5 text-right">B.Fiyat</th>
                        <th className="px-2 py-1.5 text-right">Toplam</th>
                        <th className="px-2 py-1.5 text-right">Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.rows.map((r, i) => (
                        <tr key={i} className={`border-b border-slate-100 ${r.valid ? "" : "bg-rose-50"}`}
                            data-testid={`ded-import-row-${r.row_no}`}>
                          <td className="px-2 py-1.5 font-mono text-slate-500">{r.row_no}</td>
                          <td className="px-2 py-1.5">{r.date || "—"}</td>
                          <td className="px-2 py-1.5 font-mono text-[10px] font-bold text-[#1B4776]">{r.site_code}</td>
                          <td className="px-2 py-1.5">{r.subcontractor_name}</td>
                          <td className="px-2 py-1.5">
                            <div className="font-mono text-[10px]">{r.category_code}</div>
                            {r.category_name && <div className="text-[9px] text-slate-400">{r.category_name}</div>}
                          </td>
                          <td className="px-2 py-1.5">{smartTitleCaseTR(r.description)}</td>
                          <td className="px-2 py-1.5 text-right font-mono">
                            {r.quantity} <span className="text-slate-400">{r.unit}</span>
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmtTL(r.unit_price)}</td>
                          <td className="px-2 py-1.5 text-right font-mono font-bold">{fmtTL(r.total_amount)}</td>
                          <td className="px-2 py-1.5 text-right">
                            {r.valid ? (
                              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-emerald-700">
                                <CheckCircle size={10} weight="fill" /> Geçerli
                              </span>
                            ) : (
                              <span title={r.errors.join("; ")}
                                    className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-rose-700">
                                <WarningCircle size={10} weight="fill" /> Hata
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {importPreview.errors > 0 && (
                  <div className="p-3 border-t border-slate-200 bg-rose-50/50 max-h-48 overflow-y-auto">
                    <div className="text-[11px] font-bold uppercase text-rose-800 mb-1">Hatalı Satırlar</div>
                    <ul className="space-y-0.5">
                      {importPreview.rows.filter((r) => !r.valid).map((r) => (
                        <li key={r.row_no} className="text-[11px] text-rose-700">
                          <span className="font-bold">#{r.row_no}:</span> {r.errors.join(" · ")}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}
                    className="rounded-none sm:rounded-sm">Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

// ----------------- Alt Bileşenler -----------------
function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">{children}</div>;
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card className="p-3 rounded-none sm:rounded-sm border-slate-200">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center justify-between">
        <span>{label}</span>
        {Icon && <Icon size={14} weight="duotone" className={accent} />}
      </div>
      <div className={`text-2xl font-bold mt-1 ${accent || "text-slate-800"}`}>{value}</div>
    </Card>
  );
}

function CategoriesDialog({ open, onOpenChange, cats, onChange }) {
  const confirm = useConfirm();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) { toast.error("Kategori adı gerekli"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/sub-deduction-categories`, {
        code: code.trim() || name.trim().toLowerCase().replace(/\s+/g, "_"),
        name: name.trim(),
        description: "",
      });
      toast.success("Kategori eklendi");
      setName(""); setCode("");
      onChange();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    const ok = await confirm({
      title: "Kategori Silinsin mi?",
      description: `"${c.name}" kategorisi silinecek. Kullanılıyorsa silinemez.`,
      confirmText: "Sil", variant: "danger",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/sub-deduction-categories/${c.id}`);
      toast.success("Kategori silindi");
      onChange();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none sm:rounded-sm max-h-[85vh] overflow-y-auto"
                     data-testid="ded-categories-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B4776] flex items-center gap-2">
            <Tag size={18} weight="duotone" /> Kesinti Kategorileri
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Card className="p-3 rounded-none sm:rounded-sm border-slate-200 bg-slate-50">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
              Yeni Kategori Ekle
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input placeholder="Ad (örn: Ulaşım Kesintisi)"
                     value={name} onChange={(e) => setName(e.target.value)}
                     className="sm:col-span-2 rounded-none sm:rounded-sm"
                     data-testid="ded-cat-name" />
              <div className="flex gap-2">
                <Input placeholder="Kod (opsiyonel)"
                       value={code} onChange={(e) => setCode(e.target.value)}
                       className="rounded-none sm:rounded-sm"
                       data-testid="ded-cat-code" />
                <Button onClick={add} disabled={busy}
                        className="bg-[#95C11F] hover:bg-[#7ea01a] text-white rounded-none sm:rounded-sm"
                        data-testid="ded-cat-add-btn">
                  <Plus size={14} weight="bold" />
                </Button>
              </div>
            </div>
          </Card>

          <div className="border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <th className="px-3 py-2 text-left">Ad</th>
                  <th className="px-3 py-2 text-left">Kod</th>
                  <th className="px-3 py-2 text-left">Tür</th>
                  <th className="px-3 py-2 text-right w-16">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {cats.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100" data-testid={`ded-cat-row-${c.code}`}>
                    <td className="px-3 py-2 font-medium">{c.name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{c.code}</td>
                    <td className="px-3 py-2">
                      {c.is_system ? (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase bg-[#1B4776] text-white rounded-sm">
                          Sistem
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 rounded-sm">
                          Özel
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {!c.is_system && (
                        <button onClick={() => remove(c)}
                                className="p-1.5 text-slate-500 hover:text-rose-700 hover:bg-rose-50 rounded-sm"
                                data-testid={`ded-cat-del-${c.code}`}>
                          <TrashSimple size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  className="rounded-none sm:rounded-sm">Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
